import { useCallback, useEffect, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { Button, DismissibleBanner } from "@wato/ui";
import { useApi } from "../../api/context";
import type { SampleDataStatus } from "../../api/client";
import { useUiLayer } from "./useUiLayer";

/**
 * WP-568: offers sample data on a fresh workspace with no connected channel, and keeps a
 * "Remove sample data" control once seeded (dismissible once per user, UX-19). Truth is always the server's status read.
 */
export function SampleDataBanner({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const [status, setStatus] = useState<SampleDataStatus | null>(null);
  const [channelConnected, setChannelConnected] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const layer = useUiLayer(workspaceId, "sampleDataBanner");

  useEffect(() => {
    api.getSampleData(workspaceId).then(setStatus, () => setStatus(null));
    api.getOnboardingChecklist(workspaceId).then(
      (c) => setChannelConnected(c.steps.some((s) => s.key === "channelConnected" && s.done)),
      () => undefined,
    );
  }, [api, workspaceId]);

  const run = useCallback(async (fn: () => Promise<SampleDataStatus>) => {
    setBusy(true);
    setFailed(false);
    try {
      setStatus(await fn());
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, []);

  // NOFAKE-2: sample data is never seeded automatically into a real workspace; only the explicit
  // «seed» button below creates it (tagged, removable).
  if (!status || !layer.show) return null;
  if (!status.seeded && channelConnected) return null;
  return (
    <DismissibleBanner
      id="sample-data"
      priority={40}
      testId="sample-data-banner"
      onDismiss={layer.dismiss}
      action={
        status.seeded ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => void run(() => api.removeSampleData(workspaceId)).then(layer.dismiss)}
          >
            {t("helpCenter.sampleData.remove")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => void run(() => api.seedSampleData(workspaceId))}
          >
            {t("helpCenter.sampleData.seed")}
          </Button>
        )
      }
    >
      {t(status.seeded ? "helpCenter.sampleData.seeded" : "helpCenter.sampleData.offer")}
      {failed ? <span role="alert"> {t("helpCenter.sampleData.error")}</span> : null}
    </DismissibleBanner>
  );
}
